import React, { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Bell, CalendarClock, Check, ChevronsUpDown, Download, MessageSquare, Moon, Printer, RefreshCw, Search, Share2, Sun,
 Sparkles, X } from 'lucide-react';
import { useUserSettingsContext } from '../../../context/UserSettingsContext';
import { useStreamChat } from '../../../context/StreamChatContext';
import { useNavbarActionSlot, usePageCrumbSlot, usePageHeaderSlotTarget } from '../../../context/NavbarActionsContext';
import { useTheme } from '../../../context/ThemeContext';
import NotificationCenter from '../../NotificationCenter/NotificationCenter';
import {
  Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator,
} from '../../ui/breadcrumb';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../ui/dropdown-menu';
import { SidebarTrigger } from '../../ui/sidebar';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../ui/tooltip';
import { cn } from '../../../lib/utils';
import { useUnreadNotifications } from './useUnreadNotifications';
import { openCommandPalette } from './shellEvents';

// Page-registered actions (NavbarActionsContext) cross as plain data; the
// header owns the visuals.
const ACTION_ICONS = {
  narrative: Sparkles,
  excel: Download,
  pdf: Printer,
  schedule: CalendarClock,
  share: Share2,
  refresh: RefreshCw,
};

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '');

function IconButton({ label, onClick, disabled, primary, children, badge, ...rest }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          onClick={onClick}
          disabled={disabled}
          className={cn(
            'relative inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--lodge-orange-400)]',
            'disabled:pointer-events-none disabled:opacity-40',
            primary
              ? 'bg-[var(--lodge-onyx-900)] text-[var(--lodge-white)] hover:opacity-90'
              : 'text-[var(--lodge-onyx-500)] hover:bg-[var(--surface-hover)] hover:text-[var(--lodge-onyx-900)]'
          )}
          {...rest}
        >
          {children}
          {badge > 0 && (
            <span className="absolute -right-1 -top-1 flex h-[16px] min-w-[16px] items-center justify-center rounded-full bg-[#FF7A00] px-1 text-[9px] font-bold text-white">
              {badge > 99 ? '99+' : badge}
            </span>
          )}
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom">{label}</TooltipContent>
    </Tooltip>
  );
}

/**
 * Breadcrumb model for a resolved nav trail. The last crumb doubles as a page
 * switcher across its siblings (the report group's pages, or the section's
 * items). Shared with the phone card head (Layout/mobile/MobileCardHead).
 */
export function buildCrumbs(trail) {
  const crumbs = [];
  if (!trail) return crumbs;
  if (trail.group) crumbs.push({ key: 'group', title: trail.group });
  crumbs.push({ key: trail.item.key, title: trail.item.title, to: trail.child ? trail.item.to : null });
  if (trail.child) crumbs.push({ key: trail.child.key, title: trail.child.title });
  const last = crumbs[crumbs.length - 1];
  const siblings = (trail.siblings || []).filter((s) => s.to);
  if (siblings.length > 1) last.siblings = siblings;
  return crumbs;
}

export const ACTION_ICON_MAP = ACTION_ICONS;

function Divider() {
  return <span aria-hidden className="mx-1 h-5 w-px bg-[var(--lodge-onyx-100)]" />;
}

/**
 * Sticky header inside the inset card: sidebar toggle · breadcrumb ·
 * page actions · search · notifications · chat · theme.
 */
export default function AppHeader({ trail, onToggleChat }) {
  const navigate = useNavigate();
  const { userSettings } = useUserSettingsContext();
  const { unreadMessageCount } = useStreamChat();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const pageActions = useNavbarActionSlot();
  const pageCrumb = usePageCrumbSlot();
  const setHeaderSlot = usePageHeaderSlotTarget();
  const { count: unread, refresh: refreshUnread } = useUnreadNotifications(userSettings?.email);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const bellRef = useRef(null);

  // A page-registered crumb (the module's own sections) takes the switcher
  // role; the nav trail's last crumb then reads as a plain parent.
  const crumbs = buildCrumbs(trail);
  if (pageCrumb) {
    const parent = crumbs[crumbs.length - 1];
    if (parent) {
      delete parent.siblings;
      if (!parent.to && trail?.item?.to) parent.to = trail.item.to;
    }
    crumbs.push({
      key: 'page',
      title: pageCrumb.title,
      siblings: pageCrumb.options.map((o) => ({ key: o.key, title: o.title, to: null })),
      onSelectSibling: pageCrumb.onSelect,
      currentKey: pageCrumb.options.find((o) => o.title === pageCrumb.title)?.key,
    });
  }

  return (
    <header className="app-shell-header flex shrink-0 items-center gap-2 px-3">
      <SidebarTrigger className="-ml-0.5" />
      <Divider />
      <Breadcrumb className="min-w-0">
        <BreadcrumbList className="flex-nowrap">
          {crumbs.map((c, i) => {
            const last = i === crumbs.length - 1;
            return (
              <React.Fragment key={c.key}>
                <BreadcrumbItem className={cn('min-w-0', !last && 'hidden md:inline-flex')}>
                  {last && c.siblings ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          aria-label={`${c.title} — switch page`}
                          className="-mx-1 inline-flex min-w-0 items-center gap-1 rounded-md px-1 py-0.5 font-medium text-[var(--lodge-onyx-900)] outline-none transition-colors hover:bg-[var(--lodge-onyx-50)] focus-visible:ring-2 focus-visible:ring-[var(--lodge-onyx-300)] data-[state=open]:bg-[var(--lodge-onyx-50)]"
                        >
                          <span className="truncate">{c.title}</span>
                          <ChevronsUpDown className="size-3.5 shrink-0 opacity-60" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="min-w-52">
                        {c.siblings.map((s) => {
                          const current = c.onSelectSibling ? s.key === c.currentKey : s.key === c.key;
                          return (
                            <DropdownMenuItem
                              key={s.key}
                              onSelect={() => {
                                if (current) return;
                                if (c.onSelectSibling) c.onSelectSibling(s.key);
                                else navigate(s.to);
                              }}
                              className={cn(current && 'font-semibold')}
                            >
                              {s.title}
                              {current && <Check className="ml-auto size-4" />}
                            </DropdownMenuItem>
                          );
                        })}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : last ? (
                    <BreadcrumbPage className="truncate">{c.title}</BreadcrumbPage>
                  ) : c.to ? (
                    <BreadcrumbLink asChild><Link to={c.to}>{c.title}</Link></BreadcrumbLink>
                  ) : (
                    <span className="truncate">{c.title}</span>
                  )}
                </BreadcrumbItem>
                {!last && <BreadcrumbSeparator className="hidden md:block" />}
              </React.Fragment>
            );
          })}
        </BreadcrumbList>
      </Breadcrumb>

      {pageCrumb?.back && (
        <span
          className="ml-1.5 inline-flex h-7 shrink-0 items-center rounded-full border text-[12.5px] font-semibold animate-in fade-in-0 slide-in-from-left-2 duration-300"
          style={{ background: 'var(--color-success-bg)', color: 'var(--color-success-ink)', borderColor: 'color-mix(in srgb, var(--color-success-ink) 28%, transparent)' }}
        >
          <button
            type="button"
            onClick={pageCrumb.back.onClick}
            title={pageCrumb.back.title || pageCrumb.back.label}
            className="inline-flex h-full items-center gap-1.5 rounded-l-full pl-2.5 pr-1.5 outline-none transition-colors hover:bg-[color-mix(in_srgb,var(--color-success-ink)_10%,transparent)] focus-visible:ring-2 focus-visible:ring-[var(--color-success-ink)]"
          >
            <ArrowLeft className="size-3.5" />
            {pageCrumb.back.label}
          </button>
          <button
            type="button"
            onClick={pageCrumb.back.onDismiss}
            aria-label="Dismiss"
            className="inline-flex h-full items-center rounded-r-full pl-1 pr-2 opacity-60 outline-none transition-opacity hover:opacity-100"
          >
            <X className="size-3" />
          </button>
        </span>
      )}

      {/* Page context pickers (portaled in by the page; empty otherwise). */}
      <div ref={setHeaderSlot} data-header-slot className="flex min-w-0 items-center empty:hidden" />

      <div className="ml-auto flex items-center gap-0.5">
        {pageActions.length > 0 && (
          <>
            <div className="flex items-center gap-0.5 animate-in fade-in-0 slide-in-from-right-2 duration-300">
              {pageActions.map((action) => {
                const Icon = ACTION_ICONS[action.key];
                if (action.text) {
                  // Labelled entry (e.g. "✦ Narrative Studio"): keeps the
                  // report's outlined orange button style inside the cluster.
                  return (
                    <button
                      key={action.key}
                      type="button"
                      onClick={action.onClick}
                      disabled={action.disabled}
                      title={action.label}
                      className="relative mr-1 inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border px-2.5 text-[12.5px] font-bold transition-colors border-[var(--lodge-orange-400)] text-[var(--lodge-orange-600,#b35500)] hover:bg-[var(--lodge-orange-50)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--lodge-orange-400)] disabled:pointer-events-none disabled:opacity-40"
                    >
                      {Icon && <Icon className="h-3.5 w-3.5" />}
                      <span>{action.text}</span>
                      {action.badge > 0 && <span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-[#FF7A00] ring-2 ring-[var(--shell-card,#fff)]" />}
                    </button>
                  );
                }
                return (
                  <IconButton
                    key={action.key}
                    label={action.label}
                    onClick={action.onClick}
                    disabled={action.disabled}
                    primary={action.primary}
                  >
                    {Icon && <Icon className={cn('h-4 w-4', action.spinning && 'animate-spin')} />}
                  </IconButton>
                );
              })}
            </div>
            <Divider />
          </>
        )}

        <button
          type="button"
          onClick={openCommandPalette}
          className="mr-1 hidden h-8 items-center gap-2 rounded-md border border-[var(--lodge-onyx-100)] px-2.5 text-sm text-[var(--lodge-onyx-400)] transition-colors hover:border-[var(--lodge-onyx-200)] hover:text-[var(--lodge-onyx-900)] lg:inline-flex"
          aria-label="Search reports and pages"
          data-tour="header-search"
        >
          <Search className="h-4 w-4" />
          <span className="pr-6">Search…</span>
          <kbd className="rounded border border-[var(--lodge-onyx-100)] px-1.5 font-mono text-[10px]">{isMac ? '⌘K' : 'Ctrl K'}</kbd>
        </button>
        <span className="lg:hidden">
          <IconButton label="Search" onClick={openCommandPalette}>
            <Search className="h-4 w-4" />
          </IconButton>
        </span>

        <div className="relative" ref={bellRef}>
          <IconButton
            label="Notifications"
            badge={unread}
            onClick={() => {
              setNotificationsOpen((o) => !o);
              if (!notificationsOpen) refreshUnread();
            }}
          >
            <Bell className="h-4 w-4" />
          </IconButton>
          <NotificationCenter
            isOpen={notificationsOpen}
            onClose={() => setNotificationsOpen(false)}
            onNavigate={(path) => {
              navigate(path);
              setNotificationsOpen(false);
            }}
            onNotificationRead={refreshUnread}
          />
        </div>

        <IconButton label="Team chat" badge={unreadMessageCount} onClick={onToggleChat}>
          <MessageSquare className="h-4 w-4" />
        </IconButton>

        <IconButton label={isDarkMode ? 'Light mode' : 'Dark mode'} onClick={toggleDarkMode}>
          {isDarkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </IconButton>
      </div>
    </header>
  );
}
